'use client';
import { logger } from '../../../lib/logger';

import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { useState, Suspense, FormEvent } from "react";
import { useTranslations } from 'next-intl';
import { PENDING_EMAIL_STORAGE_KEY } from '@/lib/auth-code';
import { SUPPORT_EMAIL } from '@/lib/config';
import { SigninLanguageSelect } from './SigninLanguageSelect';
import { Mail } from 'lucide-react';
import { AuthBrand } from '../AuthBrand';
import { AuthLoadingCard } from '../AuthLoadingCard';
import styles from "./signin.module.css";

/**
 * Allows users to authenticate with Google, Microsoft, GitHub, or email magic link.
 * Users must be in the allowlist or have an email domain matching an organization.
 */

function SignInForm() {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const error = searchParams.get("error");
  // Return the user to where the middleware bounced them from. Only honor a
  // same-origin relative path (single leading slash) — never an absolute or
  // protocol-relative URL, which could redirect off-site after login.
  const rawCallback = searchParams.get("callbackUrl");
  const callbackUrl = rawCallback && /^\/(?![/\\])/.test(rawCallback) ? rawCallback : "/";
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleSignIn = async (provider: string) => {
    setIsLoading(provider);
    try {
      await signIn(provider, { callbackUrl });
    } catch (error) {
      logger.error("Sign-in error:", error);
      setIsLoading(null);
    }
  };

  const handleEmailSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setEmailError(t('auth.enterEmailRequired'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError(t('errors.invalidEmail'));
      return;
    }

    setIsLoading("email");
    try {
      sessionStorage.setItem(PENDING_EMAIL_STORAGE_KEY, trimmedEmail);
      await signIn("email", { email: trimmedEmail, callbackUrl });
    } catch (error) {
      logger.error("Email sign-in error:", error);
      setEmailError(t('errors.networkError'));
      setIsLoading(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.languageRow}>
          <SigninLanguageSelect />
        </div>
        <AuthBrand />

        {error && (
          <div className={styles.errorBanner}>
            {error === "AccessDenied" && (
              <p>
                <strong>{t('errors.accessDenied')}</strong>
                <br />
                {t('auth.notAuthorized')}
                <br />
                {t('auth.requestAccess', { email: SUPPORT_EMAIL })}
              </p>
            )}
            {error === "ServiceUnavailable" && (
              <p>
                <strong>{t('auth.temporarilyUnavailable')}</strong>
                <br />
                {t('auth.tryAgainMinutes')}
              </p>
            )}
            {error !== "AccessDenied" && error !== "ServiceUnavailable" && (
              <p>{t('auth.signInFailed')}</p>
            )}
          </div>
        )}

        <div className={styles.providersContainer}>
          <button
            onClick={() => handleSignIn("google")}
            disabled={isLoading !== null}
            className={styles.providerButton}
          >
            {isLoading === "google" ? (
              <span className={styles.spinner}></span>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z"
                  fill="#4285F4"
                />
                <path
                  d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z"
                  fill="#34A853"
                />
                <path
                  d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49L4.405 11.9z"
                  fill="#FBBC05"
                />
                <path
                  d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z"
                  fill="#EA4335"
                />
              </svg>
            )}
            <span>{t('auth.signInWith', { provider: 'Google' })}</span>
          </button>

          {process.env.NEXT_PUBLIC_AZURE_AD_ENABLED === "true" && (
            <button
              onClick={() => handleSignIn("azure-ad")}
              disabled={isLoading !== null}
              className={styles.providerButton}
            >
              {isLoading === "azure-ad" ? (
                <span className={styles.spinner}></span>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 23 23"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0 0h10.87v10.87H0z" fill="#f25022" />
                  <path d="M12.13 0H23v10.87H12.13z" fill="#7fba00" />
                  <path d="M0 12.13h10.87V23H0z" fill="#00a4ef" />
                  <path d="M12.13 12.13H23V23H12.13z" fill="#ffb900" />
                </svg>
              )}
              <span>{t('auth.signInWith', { provider: 'Microsoft' })}</span>
            </button>
          )}

          {process.env.NEXT_PUBLIC_GITHUB_ENABLED === "true" && (
            <button
              onClick={() => handleSignIn("github")}
              disabled={isLoading !== null}
              className={styles.providerButton}
            >
              {isLoading === "github" ? (
                <span className={styles.spinner}></span>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"
                    fill="#24292e"
                  />
                </svg>
              )}
              <span>{t('auth.signInWith', { provider: 'GitHub' })}</span>
            </button>
          )}
        </div>

        <div className={styles.divider}>
          <span className={styles.dividerText}>or</span>
        </div>

        <form onSubmit={handleEmailSignIn} className={styles.emailForm}>
          <div className={styles.inputWrapper}>
            <input
              type="email"
              placeholder={t('auth.enterEmail')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading !== null}
              className={styles.emailInput}
              autoComplete="email"
            />
          </div>
          {emailError && (
            <p className={styles.emailError}>{emailError}</p>
          )}
          <button
            type="submit"
            disabled={isLoading !== null}
            className={styles.emailButton}
          >
            {isLoading === "email" ? (
              <span className={styles.spinner}></span>
            ) : (
              <Mail size={18} aria-hidden />
            )}
            <span>{t('auth.continueWithEmail')}</span>
          </button>
        </form>
      </div>
    </div>
  );
}

export default function SignInPage() {
  return (
    <Suspense fallback={<AuthLoadingCard />}>
      <SignInForm />
    </Suspense>
  );
}
